import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ActivationBadge } from '../../components/ActivationPanel'
import { FormCheckbox, FormField } from '../../components/FormField'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { InactiveToggle } from '../../components/InactiveToggle'
import { ApiError } from '../../lib/api'
import { apiErrorMessage } from '../../lib/errors'
import { formatBusinessDate } from '../../lib/format'
import { inactiveCount, rowsShown } from '../../lib/inactive'
import { formatPhoneTyping } from '../../lib/phone'
import { NO_VIEW, type TableColumn } from '../../lib/tableView'
import { useTableView } from '../../lib/useTableView'
import {
  addAgent,
  listAgents,
  setAgentActivation,
  updateAgent,
  type BrokerAgent,
  type BrokerAgentPayload,
} from './api'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { messageFor, toAgentFormErrors, withoutAgentErrorsFor, type FormErrors } from './errors'

const emptyText = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  phoneExtension: '',
}

type AgentTextField = keyof typeof emptyText

type AgentForm = typeof emptyText & { optedOut: boolean }

const blankForm: AgentForm = { ...emptyText, optedOut: false }

const noErrors: FormErrors = { fields: {}, summary: [] }

const isChangedElsewhere = (caught: unknown) =>
  caught instanceof ApiError && caught.codes.includes(CHANGED_ELSEWHERE)

/** One editor at a time: nothing typed into one form is lost by opening another. */
type Editor = { kind: 'closed' } | { kind: 'add' } | { kind: 'edit'; agent: BrokerAgent }

function toForm(agent: BrokerAgent): AgentForm {
  return {
    firstName: agent.firstName,
    lastName: agent.lastName,
    title: agent.title ?? '',
    email: agent.email ?? '',
    phone: agent.phone ?? '',
    phoneExtension: agent.phoneExtension ?? '',
    optedOut: agent.optedOutOfBroadcasts,
  }
}

const fullName = (agent: BrokerAgent) => `${agent.firstName} ${agent.lastName}`

/** Whether the availability emails reach an agent: what the third column sorts and filters by. */
const RECEIVES = ['Receives', 'OptedOut'] as const
const receives = (agent: BrokerAgent) => (agent.broadcastOptedOutAt === null ? 'Receives' : 'OptedOut')

/** The agents we work with first, then the ones stood down — whatever the column (TRKC-74). */
const inPlayFirst = (agent: BrokerAgent) => (agent.active ? 0 : 1)

/** What each column of the agents sorts and filters by (TRKC-110). */
const AGENT_COLUMNS: readonly TableColumn<BrokerAgent>[] = [
  {
    key: 'agent',
    sortValue: fullName,
    filter: { kind: 'text', text: (agent) => [fullName(agent), agent.title ?? ''].join(' ') },
  },
  {
    key: 'contact',
    sortValue: (agent) => agent.email,
    filter: { kind: 'text', text: (agent) => [agent.email ?? '', agent.phone ?? ''].join(' ') },
  },
  {
    key: 'broadcasts',
    sortValue: (agent) => RECEIVES.indexOf(receives(agent)),
    filter: { kind: 'choice', values: RECEIVES, matches: (agent, chosen) => receives(agent) === chosen },
  },
]

/** The heading each column's filter box is named after. */
const AGENT_HEADINGS: Record<string, string> = {
  agent: 'agents.agent',
  contact: 'agents.contact',
  broadcasts: 'agents.broadcasts',
}

/** No agents yet: one array, so the view is not recomputed for an empty one every render. */
const NO_AGENTS: BrokerAgent[] = []

/** The API's order: who we work with first, then by last name and first name. */
function byStandingThenName(a: BrokerAgent, b: BrokerAgent): number {
  if (a.active !== b.active) {
    return a.active ? -1 : 1
  }

  return a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
}

/**
 * A broker's agents — the people behind the switchboard (TRKC-85): who
 * calls about a load, who signs the rate confirmation, who the availability emails go
 * to. The broker's own phone and email above stay the company's.
 *
 * The panel owns its rows and never re-reads the broker. Every write answers with the
 * agent exactly as stored, and nothing else on the list depends on it, so the answer is
 * merged in place: a re-read of the whole page would throw away whatever was typed
 * into the broker form above, and two re-reads racing each other could land in the
 * wrong order (both findings paid for on #13 and #23).
 *
 * An edit carries the version of the agent its form was opened on, and one made on an
 * older reading is refused (review finding on #24: a form left open would otherwise put
 * back everything a newer save changed — a person's opt-out included). That refusal is
 * the one time the panel re-reads, and only its own list: the rows then show what is
 * saved, while the form keeps what was typed, still on the old version — so saving it
 * again cannot quietly undo the other save either. Cancel and edit again starts from it.
 */
export function AgentsPanel({ brokerId }: { brokerId: string }) {
  const { t, i18n } = useTranslation()

  const [agents, setAgents] = useState<BrokerAgent[] | null>(null)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)

  // The agents sort and filter by their columns (TRKC-110), and start over for another broker.
  // Inactive agents are held back until asked for (TRKC-110 item 2), and the choice starts
  // over for another broker, with the view.
  const [showInactive, setShowInactive] = useState(false)
  const listId = useId()
  const listed = useMemo(() => rowsShown(agents ?? NO_AGENTS, showInactive), [agents, showInactive])
  const view = useTableView(listed, AGENT_COLUMNS, { rank: inPlayFirst })
  const { replace: replaceView } = view
  useEffect(() => {
    replaceView(NO_VIEW)
    setShowInactive(false)
  }, [brokerId, replaceView])
  const receiveOptions = useMemo(
    () => [
      { value: 'Receives', label: t('agents.receives') },
      { value: 'OptedOut', label: t('agents.optedOutShort') },
    ],
    [t],
  )
  const [editor, setEditor] = useState<Editor>({ kind: 'closed' })
  const [form, setForm] = useState<AgentForm>(blankForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The name the last save was about, for the one-line confirmation. Cleared the moment
  // anything else starts: "Saved Mike" beside a form now editing Ana would be a lie.
  const [savedName, setSavedName] = useState<string | null>(null)
  const [busyAgentId, setBusyAgentId] = useState<string | null>(null)
  const [activationFailure, setActivationFailure] = useState<{ error: unknown } | null>(null)

  // Only the latest read writes the rows: an older answer landing last would put back a
  // list the server has already moved past (#23's lesson).
  const latestRead = useRef(0)

  const readAgents = useCallback(async () => {
    const read = ++latestRead.current

    try {
      const rows = await listAgents(brokerId)
      if (read === latestRead.current) {
        setAgents(rows)
        setLoadFailure(null)
      }
    } catch (caught) {
      if (read === latestRead.current) {
        setLoadFailure({ error: caught })
      }
    }
  }, [brokerId])

  useEffect(() => {
    setAgents(null)
    setLoadFailure(null)
    void readAgents()

    return () => {
      // An answer about a broker this panel no longer shows is dropped.
      latestRead.current += 1
    }
  }, [readAgents])

  /** Puts a saved agent where the API would list it. */
  function merge(saved: BrokerAgent) {
    setAgents((current) => {
      if (current === null || saved.brokerId !== brokerId) {
        return current
      }

      return [...current.filter((row) => row.id !== saved.id), saved].sort(byStandingThenName)
    })
  }

  function open(next: Editor) {
    setEditor(next)
    setForm(next.kind === 'edit' ? toForm(next.agent) : blankForm)
    setErrors(noErrors)
    setSavedName(null)
    setActivationFailure(null)
  }

  function set(field: AgentTextField, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    // A complaint stops applying the moment its box is edited.
    setErrors((current) => withoutAgentErrorsFor(current, field))
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (editor.kind === 'closed') {
      return
    }

    setErrors(noErrors)

    const payload: BrokerAgentPayload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      title: blankToNull(form.title),
      email: blankToNull(form.email),
      phone: blankToNull(form.phone),
      phoneExtension: blankToNull(form.phoneExtension),
      optedOutOfBroadcasts: form.optedOut,
    }

    setIsSubmitting(true)

    try {
      const saved =
        editor.kind === 'add'
          ? await addAgent(brokerId, payload)
          : await updateAgent(brokerId, editor.agent.id, payload, editor.agent.updatedAt)

      merge(saved)
      setEditor({ kind: 'closed' })
      setSavedName(fullName(saved))
    } catch (caught) {
      // Every broken rule at once, each under its own box — a duplicate email names the
      // agent who already has it.
      setErrors(toAgentFormErrors(caught))

      // Somebody saved this agent after the form was opened: the rows show what they
      // saved, and the form stays as typed, on the version it was opened on.
      if (isChangedElsewhere(caught)) {
        void readAgents()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleActivation(agent: BrokerAgent) {
    // Only the half that takes somebody out of play asks. Coming back is harmless.
    if (agent.active && !window.confirm(t('agents.deactivateConfirm', { name: fullName(agent) }))) {
      return
    }

    setBusyAgentId(agent.id)
    setActivationFailure(null)
    setSavedName(null)

    try {
      merge(await setAgentActivation(brokerId, agent.id, !agent.active))
    } catch (caught) {
      // agent_email_taken lands here: the address was given to somebody else while this
      // agent was away, and the sentence names who holds it now.
      setActivationFailure({ error: caught })
    } finally {
      setBusyAgentId(null)
    }
  }

  // Resolved at render: the codes live in state, so a language switch re-translates a
  // complaint already on screen.
  const fieldText = (name: AgentTextField) => {
    const code = errors.fields[name]
    return code === undefined
      ? undefined
      : messageFor(code, t, 'agents.saveError', errors.subjects?.[code])
  }

  const editorOpen = editor.kind !== 'closed'
  const rowsLocked = editorOpen || busyAgentId !== null

  const phoneText = (agent: BrokerAgent) =>
    agent.phoneExtension === null
      ? agent.phone
      : t('agents.phoneWithExtension', { phone: agent.phone, extension: agent.phoneExtension })

  const broadcastText = (agent: BrokerAgent) => {
    if (agent.broadcastOptedOutAt !== null) {
      // The day is the fact; the minute they asked is noise on a directory.
      return t('agents.optedOutOn', {
        when: formatBusinessDate(agent.broadcastOptedOutAt, i18n.language),
      })
    }

    // Somebody we stopped working with receives nothing, whatever they asked for.
    if (!agent.active) {
      return '—'
    }

    return t('agents.receives')
  }

  return (
    <section className="fleet agents" aria-labelledby="agents-title">
      <header className="page__header--row fleet__header">
        <h2 id="agents-title">{t('agents.title')}</h2>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => open({ kind: 'add' })}
          disabled={editorOpen || agents === null || loadFailure !== null}
        >
          {t('agents.add')}
        </button>
      </header>

      <p className="page__subtitle">{t('agents.subtitle')}</p>

      {savedName !== null && (
        <p className="page__subtitle" role="status">
          {t('agents.saved', { name: savedName })}
        </p>
      )}

      {activationFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(activationFailure.error, t, 'agents.activationError')}
        </p>
      )}

      {editorOpen && (
        <form
          className="form form--stacked agents__form"
          onSubmit={(event) => void handleSubmit(event)}
          aria-labelledby="agents-editor-title"
          noValidate
        >
          {errors.summary.length > 0 && (
            <div className="alert" role="alert">
              {errors.summary.map((code) => (
                <p key={code}>
                  {/* Not the generic "reload and try again": reloading the page would
                      throw away whatever is typed into the broker form above. */}
                  {code === CHANGED_ELSEWHERE && editor.kind === 'edit'
                    ? t('agents.changedElsewhere', { name: fullName(editor.agent) })
                    : messageFor(code, t, 'agents.saveError', errors.subjects?.[code])}
                </p>
              ))}
            </div>
          )}

          <fieldset className="fieldset">
            <legend id="agents-editor-title">
              {editor.kind === 'edit'
                ? t('agents.editAgentTitle', { name: fullName(editor.agent) })
                : t('agents.newAgent')}
            </legend>

            {/* Every id carries the agent- prefix: the broker form on the same page
                already owns "email" and "phone". */}
            <FormField
              name="agent-firstName"
              label={t('agents.firstName')}
              value={form.firstName}
              onChange={(value) => set('firstName', value)}
              error={fieldText('firstName')}
              maxLength={100}
              required
            />
            <FormField
              name="agent-lastName"
              label={t('agents.lastName')}
              value={form.lastName}
              onChange={(value) => set('lastName', value)}
              error={fieldText('lastName')}
              maxLength={100}
              required
            />
            <FormField
              name="agent-title"
              label={t('agents.jobTitle')}
              value={form.title}
              onChange={(value) => set('title', value)}
              error={fieldText('title')}
              maxLength={100}
            />
            <FormField
              name="agent-email"
              label={t('agents.email')}
              value={form.email}
              onChange={(value) => set('email', value)}
              error={fieldText('email')}
              type="email"
              maxLength={256}
              required
            />
            <FormField
              name="agent-phone"
              label={t('agents.phone')}
              value={form.phone}
              onChange={(value) => set('phone', formatPhoneTyping(form.phone, value))}
              error={fieldText('phone')}
              type="tel"
              placeholder="(513) 495-2000"
              required
            />
            <FormField
              name="agent-phoneExtension"
              label={t('agents.phoneExtension')}
              value={form.phoneExtension}
              onChange={(value) => set('phoneExtension', value)}
              error={fieldText('phoneExtension')}
              maxLength={16}
              placeholder="4412"
              width="narrow"
            />
            <FormCheckbox
              name="agent-optedOut"
              label={t('agents.optedOut')}
              checked={form.optedOut}
              onChange={(checked) => setForm((current) => ({ ...current, optedOut: checked }))}
            />
          </fieldset>

          <div className="form__actions">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setEditor({ kind: 'closed' })
                setErrors(noErrors)
              }}
              disabled={isSubmitting}
            >
              {t('agents.cancel')}
            </button>
            <button type="submit" className="button" disabled={isSubmitting}>
              {isSubmitting ? t('agents.saving') : t('agents.save')}
            </button>
          </div>
        </form>
      )}

      <div id={listId}>
        {loadFailure !== null ? (
          <p className="alert" role="alert">
            {apiErrorMessage(loadFailure.error, t, 'agents.loadError')}
          </p>
        ) : agents === null ? (
          <p className="placeholder">{t('agents.loading')}</p>
        ) : agents.length === 0 ? (
          <p className="placeholder">{t('agents.empty')}</p>
        ) : listed.length === 0 ? (
          <p className="placeholder">{t('agents.noneInUse')}</p>
        ) : (
          // Cards under 720px (index.css); above it, anything the columns cannot hold
          // scrolls inside the panel rather than pushing the page sideways — the same box
          // every other table sits in (TRKC-87).
          <TableScroll label={t('agents.title')}>
            <table className="table agents__table">
              <caption className="visually-hidden">{t('agents.title')}</caption>
              <thead>
                <tr>
                  {AGENT_COLUMNS.map((column) => (
                    <SortableHeader key={column.key} view={view} column={column.key}>
                      {t(AGENT_HEADINGS[column.key] ?? column.key)}
                    </SortableHeader>
                  ))}
                  <th scope="col">
                    <span className="visually-hidden">{t('agents.actions')}</span>
                  </th>
                </tr>
                <tr className="table__filters">
                  {AGENT_COLUMNS.map((column) => (
                    <FilterCell
                      key={column.key}
                      view={view}
                      column={column}
                      label={t(AGENT_HEADINGS[column.key] ?? column.key)}
                      options={column.key === 'broadcasts' ? receiveOptions : undefined}
                    />
                  ))}
                  <td />
                </tr>
              </thead>
              <tbody>
                {view.rows.length === 0 && (
                  <tr>
                    <td colSpan={AGENT_COLUMNS.length + 1} className="placeholder">
                      {t('table.noneMatch')}
                    </td>
                  </tr>
                )}
                {view.rows.map((agent) => {
                  const name = fullName(agent)

                  return (
                    <tr key={agent.id} className={historyRow(!agent.active)}>
                      <td>
                        {name}
                        {!agent.active && ' '}
                        <ActivationBadge active={agent.active} />
                        {agent.title !== null && (
                          <span className="muted agents__line">{agent.title}</span>
                        )}
                      </td>
                      {/* One contact card per person — the address first, the number under
                          it — so neither has to break mid-word to share a row with five
                          columns (found looking at the screen, not by a test). */}
                      <td>
                        {agent.email !== null && <span className="agents__email">{agent.email}</span>}
                        {agent.phone !== null && (
                          <span
                            className={
                              agent.email === null
                                ? 'agents__phone'
                                : 'muted agents__line agents__phone'
                            }
                          >
                            {phoneText(agent)}
                          </span>
                        )}
                        {agent.email === null && agent.phone === null && '—'}
                      </td>
                      <td>
                        {/* A card has no header row to say what "Receives them" is about.
                            Hidden from assistive tech, which reads the column header. */}
                        <span className="agents__cell-label" aria-hidden="true">
                          {t('agents.broadcasts')}
                        </span>
                        {broadcastText(agent)}
                      </td>
                      <td>
                        <span className="agents__actions">
                          <button
                            type="button"
                            className="button button--quiet"
                            aria-label={t('agents.editNamed', { name })}
                            onClick={() => open({ kind: 'edit', agent })}
                            disabled={rowsLocked}
                          >
                            {t('agents.edit')}
                          </button>
                          <button
                            type="button"
                            className="button button--quiet"
                            aria-label={
                              agent.active
                                ? t('agents.deactivateNamed', { name })
                                : t('agents.reactivateNamed', { name })
                            }
                            onClick={() => void handleActivation(agent)}
                            disabled={rowsLocked}
                          >
                            {busyAgentId === agent.id
                              ? t('agents.saving')
                              : agent.active
                                ? t('agents.deactivate')
                                : t('agents.reactivate')}
                          </button>
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </div>
      {agents !== null && listed.length > 0 && <FilterSummary view={view} />}
      {agents !== null && (
        <InactiveToggle
          kind="agents"
          controls={listId}
          count={inactiveCount(agents)}
          shown={showInactive}
          onToggle={() => setShowInactive((shown) => !shown)}
        />
      )}
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
